import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import PageDialog from "components/Dialogs/PageDialog";
import { useNavigate } from "react-router-dom";
import {
  CreateSportEquipmentInput,
  FileStoreEntity,
  GetSportEquipmentsDocument,
  ImageStoreEntity,
  SportEquipmentEntity, TechnicalCondition,
  useCreateSportEquipmentMutation
} from "graphql/graphQlApiHooks";
import { FormikHelpers, FormikProvider, useFormik } from "formik";
import { PageDialogActions } from "components/Dialogs/PageDialogActions";
import { useSnackbar } from "notistack";
import dayjs, { Dayjs } from "dayjs";
import { Alert, Box, Button, Card, DialogActions, DialogContent, Grid, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import StickyBox from "react-sticky-box";
import { isEqual } from "helpers";
import { useDialog } from "providers/DialogProvider";
import { SportEquipmentAboutInfo } from "../views/SportEquipmentAboutInfo";
import { SportEquipmentLocation } from "../views/SportEquipmentLocation";
import { SportEquipmentCategory } from "../views/SportEquipmentCategory";
import { SportEquipmentPhotos } from "../views/SportEquipmentPhotos";
import { SportEquipmentDocuments } from "../views/SportEquipmentDocuments";
import { BarcodePhotoUpload } from "../views/BarcodePhotoUpload";
import {
  useGetSportEquipmentInputVars
} from "../../../../../../store/reactiveVarsStores/spor-equipmnts/getSportEquipmentsInput";
import CommissioningDate from "../views/СommissioningDate";
import ErrorsStepper from "../views/ErrorsStepper";
import { FormikErrors } from "formik/dist/types";

interface ActionsProps {
  isSubmitting: boolean;
  handleSubmit: () => void;
  errors: FormikErrors<CreateSportEquipmentInput>;
  isValid: boolean;
  sportEquipment?: SportEquipmentEntity;
}
const Actions = ({ isSubmitting, handleSubmit, errors, isValid, sportEquipment }: ActionsProps) => {
  // Проверяем условие: если статус "Не исправно", а failureReason пустой - кнопка должна быть disabled
  const isFailureReasonRequired = sportEquipment?.technicalCondition === TechnicalCondition.NotWorking;
  const isFailureReasonEmpty = !sportEquipment?.failureReason || sportEquipment?.failureReason.trim() === '';
  // Проверяем условие: если спортивный объект выбран, оборудование используется и отсутствует спортзона - кнопка должна быть disabled
  const isInUseWithoutSportZone = sportEquipment?.sportObject && sportEquipment?.inUse && (!sportEquipment?.sportZone || !sportEquipment?.sportSubZone);
  const isDisabled = isSubmitting || !isValid || (isFailureReasonRequired && isFailureReasonEmpty) || isInUseWithoutSportZone;

  return (
    <>
      <PageDialogActions>

        <ErrorsStepper errors={errors} />

        <Box
          display={"flex"}
          flex={1}
        />

        <LoadingButton
          loading={isSubmitting}
          disabled={isDisabled}
          size='small'
          onClick={() => handleSubmit()}
          variant='contained'
        >
          Добавить
        </LoadingButton>
      </PageDialogActions>
    </>
  );
};

export interface ISportEquipmentDocuments {
  certificate: FileStoreEntity[] | null,
  commissioningCertificate: FileStoreEntity[] | null,
  guarantee: FileStoreEntity[] | null,
  manual: FileStoreEntity[] | null,
  passport: FileStoreEntity[] | null,
  otherDocuments: FileStoreEntity[],
}

const initialSportEquipment = {
  name: "",
  accountingName: "",
  inventoryNumber: "",
  technicalCondition: TechnicalCondition.Working,
  inUse: true,
  isEditable: true,
  commissioningDate: dayjs().format("YYYY-MM-DD"),
  length: null,
  width: null,
  height: null,
  weight: null
} as SportEquipmentEntity;

const AddSportEquipment = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { getInput } = useGetSportEquipmentInputVars();
  const [dialog, closeDialog] = useDialog();


  const [createSportEquipment] = useCreateSportEquipmentMutation();


  const [sportEquipment, setSportEquipment] = useState<SportEquipmentEntity>(initialSportEquipment);

  const onClose = () => {
    if (!isEqual(initialSportEquipment, sportEquipment)) {
      dialog({
        title: "Данные будут утеряны",
        children: <>
          <DialogContent
            dividers
          >
            <Alert severity={"warning"}>
              Вы уверены что хотите закрыть диалог добавления нового оборудования?
              Все введённые данные будут утерены!
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              size={"small"}
              onClick={() => {
                navigate("/equipments");
                closeDialog();
              }}
              color={"inherit"}
              variant={"contained"}
            >
              Закрыть диалог, с потерей данных
            </Button>
            <Button
              size={"small"}
              onClick={() => {
                closeDialog();
              }}
              color={"primary"}
              autoFocus
              variant={"contained"}
            >
              Продолжить добавление
            </Button>
          </DialogActions>
        </>
      });
    } else {
      navigate("/equipments");
    }
  };

  const CreateSportEquipmentSchema = useMemo(() => Yup.object().shape({
    name: Yup.string()
      .required("Укажите название оборудования")
      .test('not-empty', 'Укажите название оборудования', (value) => {
        return value !== null && value !== undefined && value.trim() !== '';
      }),
    accountingName: Yup.string()
      .required("Укажите бухгалтерское название оборудования")
      .test('not-empty', 'Укажите бухгалтерское название оборудования', (value) => {
        return value !== null && value !== undefined && value.trim() !== '';
      }),
    inventoryNumber: Yup.string()
      .required("Укажите инвентарный номер")
      .test('not-empty', 'Укажите инвентарный номер', (value) => {
        return value !== null && value !== undefined && value.trim() !== '';
      }),
    commissioningDate: Yup.string()
      .required("Укажите дату ввода в эксплуатацию")
      .nullable()
      .test('not-empty', 'Укажите дату ввода в эксплуатацию', (value) => {
        return value !== null && value !== undefined && value !== '';
      }),

    failureReason: Yup.string()
      .nullable()
      .when('technicalCondition', {
        is: TechnicalCondition.NotWorking,
        then: (schema) => schema
          .required("Укажите причину неисправности")
          .test('not-empty', 'Укажите причину неисправности', (value) => {
            return value !== null && value !== undefined && value.trim() !== '';
          }),
        otherwise: (schema) => schema
      }),

    sportObjectId: Yup.string()
      .required("Выберите спортивный объект")
      .nullable()
      .test('not-null', 'Выберите спортивный объект', (value) => {
        return value !== null && value !== undefined;
      })
  }), []);

  const formik = useFormik({
    initialValues: {
      name: "",
      accountingName: "",
      inventoryNumber: "",
      description: undefined,
      length: null,
      width: null,
      height: null,
      weight: null,
      inUse: true,
      availableForTransfer: false,
      isInformationComplete: false,
      technicalCondition: TechnicalCondition.Working,
      failureReason: undefined,
      commissioningDate: null,

      sportCategoryId: undefined,
      sportKindId: undefined,
      equipmentTypeId: undefined,

      sportObjectId: undefined,
      sportZoneId: undefined,
      sportSubZoneId: undefined,

      specificationValues: [],

      certificateIds: [],
      commissioningCertificateIds: [],
      guaranteeIds: [],
      manualIds: [],
      passportIds: [],
      otherDocumentsIds: [],

      mainPhotoId: undefined,
      photosIds: [],
      barcodePhotoId: undefined
    } as CreateSportEquipmentInput,
    validationSchema: CreateSportEquipmentSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: false,
    onSubmit: (
      values: CreateSportEquipmentInput,
      { setSubmitting, setErrors }: FormikHelpers<CreateSportEquipmentInput>
    ) => {
      // Дополнительная проверка: если статус "Не исправно", а failureReason пустой - блокируем сохранение
      if (sportEquipment?.technicalCondition === TechnicalCondition.NotWorking) {
        const failureReason = sportEquipment?.failureReason;
        if (!failureReason || failureReason.trim() === '') {
          setErrors({ failureReason: 'Укажите причину неисправности' });
          setSubmitting(false);
          return;
        }
      }

      createSportEquipment({
        variables: {
          input: { ...values }
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GetSportEquipmentsDocument,
            fetchPolicy: "network-only",
            variables: {
              input: getInput()
            }
          }
        ]
      })
        .then((res) => {
          navigate("/equipments");
          enqueueSnackbar(
            `Обрудование ${res?.data?.createSportEquipment?.name}, успешно добавлено.`
          );
        })
        .catch(({ message }) => {
          enqueueSnackbar(
            message.replace("Unexpected error value: ", "").replace(/\"/g, ""),
            {
              variant: "error"
            }
          );

        })
        .finally(() => setSubmitting(false));
    }
  });

  const { errors, touched, isSubmitting, handleSubmit, setValues, handleBlur, values, isValid, setFieldValue } = formik;

  // Обертка для setSportEquipment, которая также синхронизирует значения с formik
  const handleSetSportEquipment = useCallback((updater: React.SetStateAction<SportEquipmentEntity>) => {
    setSportEquipment((prevState) => {
      const newState = typeof updater === 'function' ? updater(prevState) : updater;
      
      // Синхронизируем критические поля с formik сразу при изменении
      if (newState) {
        // Обновляем поля напрямую в formik для немедленной валидации
        if (newState.name !== undefined && newState.name !== prevState?.name) {
          setFieldValue('name', newState.name === null ? "" : newState.name);
        }
        if (newState.accountingName !== undefined && newState.accountingName !== prevState?.accountingName) {
          setFieldValue('accountingName', newState.accountingName === null ? "" : newState.accountingName);
        }
        if (newState.inventoryNumber !== undefined && newState.inventoryNumber !== prevState?.inventoryNumber) {
          setFieldValue('inventoryNumber', newState.inventoryNumber || "");
        }
        if (newState.description !== undefined && newState.description !== prevState?.description) {
          setFieldValue('description', newState.description);
        }
        if (newState.inUse !== undefined && newState.inUse !== prevState?.inUse) {
          setFieldValue('inUse', newState.inUse);
        }
        if (newState.availableForTransfer !== undefined && newState.availableForTransfer !== prevState?.availableForTransfer) {
          setFieldValue('availableForTransfer', newState.availableForTransfer);
        }
        if (newState.isInformationComplete !== undefined && newState.isInformationComplete !== prevState?.isInformationComplete) {
          setFieldValue('isInformationComplete', newState.isInformationComplete);
        }
        if (newState.technicalCondition !== undefined && newState.technicalCondition !== prevState?.technicalCondition) {
          setFieldValue('technicalCondition', newState.technicalCondition);
        }
        if (newState.failureReason !== undefined && newState.failureReason !== prevState?.failureReason) {
          setFieldValue('failureReason', newState.failureReason || "");
        }
        if (newState.commissioningDate !== undefined && newState.commissioningDate !== prevState?.commissioningDate) {
          setFieldValue('commissioningDate', dayjs(newState.commissioningDate).format("DD.MM.YYYY") === "Invalid Date" ? "" : dayjs(newState.commissioningDate).format("DD.MM.YYYY"));
        }
        if (newState.sportObject !== undefined && newState.sportObject?.id !== prevState?.sportObject?.id) {
          setFieldValue('sportObjectId', newState.sportObject?.id || null);
        }
        if (newState.sportZone !== undefined && newState.sportZone?.id !== prevState?.sportZone?.id) {
          setFieldValue('sportZoneId', newState.sportZone?.id || null);
        }
        if (newState.sportSubZone !== undefined && newState.sportSubZone?.id !== prevState?.sportSubZone?.id) {
          setFieldValue('sportSubZoneId', newState.sportSubZone?.id || null);
        }
        if (newState.sportCategoryObject?.id !== undefined && newState.sportCategoryObject?.id !== prevState?.sportCategoryObject?.id) {
          setFieldValue('sportCategoryId', newState.sportCategoryObject?.id || null);
        }
        if (newState.sportKindObject?.id !== undefined && newState.sportKindObject?.id !== prevState?.sportKindObject?.id) {
          setFieldValue('sportKindId', newState.sportKindObject?.id || null);
        }
        if (newState.equipmentTypeObject?.id !== undefined && newState.equipmentTypeObject?.id !== prevState?.equipmentTypeObject?.id) {
          setFieldValue('equipmentTypeId', newState.equipmentTypeObject?.id || null);
        }
        if (newState.photos !== undefined && newState.photos !== prevState?.photos) {
          setFieldValue('photosIds', newState.photos?.map(photo => photo.id) || []);
          // Если есть фото и нет главного фото, устанавливаем первое фото как главное
          if (newState.photos?.length > 0 && !newState.mainPhoto) {
            setFieldValue('mainPhotoId', newState.photos[0]?.id || undefined);
          }
        }
        if (newState.mainPhoto !== undefined && newState.mainPhoto !== prevState?.mainPhoto) {
          setFieldValue('mainPhotoId', newState.mainPhoto?.id || undefined);
        }
        if (newState.barcodePhoto !== undefined && newState.barcodePhoto !== prevState?.barcodePhoto) {
          setFieldValue('barcodePhotoId', newState.barcodePhoto?.id || undefined);
        }
      }
      
      return newState;
    });
  }, [setFieldValue]);

  useEffect(() => {
    if (sportEquipment) {
      setValues(prevState => ({
        ...prevState,
        ...(sportEquipment?.id !== undefined && { id: sportEquipment.id }),
        ...(sportEquipment?.name !== undefined && { name: sportEquipment.name }),
        ...(sportEquipment?.accountingName !== undefined && { accountingName: sportEquipment.accountingName }),
        ...(sportEquipment?.inventoryNumber !== undefined && { inventoryNumber: sportEquipment.inventoryNumber }),
        ...(sportEquipment?.description !== undefined && { description: sportEquipment.description }),
        ...(sportEquipment?.inUse !== undefined && { inUse: sportEquipment.inUse }),
        ...(sportEquipment?.availableForTransfer !== undefined && { availableForTransfer: sportEquipment.availableForTransfer }),
        ...(sportEquipment?.isInformationComplete !== undefined && { isInformationComplete: sportEquipment.isInformationComplete }),
        ...(sportEquipment?.technicalCondition !== undefined && { technicalCondition: sportEquipment.technicalCondition }),
        ...(sportEquipment?.failureReason !== undefined && { failureReason: (sportEquipment?.technicalCondition === TechnicalCondition.NotWorking) ? sportEquipment.failureReason : "" }),
        ...(sportEquipment?.commissioningDate !== undefined && {
          commissioningDate: dayjs(sportEquipment.commissioningDate).format("DD.MM.YYYY") === 'Invalid Date' ? "" : dayjs(sportEquipment.commissioningDate).format("DD.MM.YYYY")
        }),
        ...(sportEquipment?.sportCategoryObject?.id && { sportCategoryId: sportEquipment.sportCategoryObject.id }),
        ...(sportEquipment?.sportKindObject?.id && { sportKindId: sportEquipment.sportKindObject.id }),
        ...(sportEquipment?.equipmentTypeObject?.id && { equipmentTypeId: sportEquipment.equipmentTypeObject.id }),
        ...(sportEquipment?.sportObject?.id && { sportObjectId: sportEquipment.sportObject.id }),
        ...(sportEquipment?.sportZone?.id && { sportZoneId: sportEquipment.sportZone.id }),
        ...(sportEquipment?.sportSubZone?.id && { sportSubZoneId: sportEquipment.sportSubZone.id }),
        ...(sportEquipment?.certificate?.length > 0 && { certificateIds: sportEquipment.certificate?.map(item => item.id) }),
        ...(sportEquipment?.commissioningCertificates?.length > 0 && { commissioningCertificateIds: sportEquipment.commissioningCertificates?.map(item => item.id) }),
        ...(sportEquipment?.guarantee?.length > 0 && { guaranteeIds: sportEquipment.guarantee?.map(item => item.id) }),
        ...(sportEquipment?.manual?.length > 0 && { manualIds: sportEquipment.manual?.map(item => item.id) }),
        ...(sportEquipment?.passport?.length > 0 && { passportIds: sportEquipment.passport?.map(item => item.id) }),
        ...(sportEquipment?.otherDocuments?.length > 0 && { otherDocumentsIds: sportEquipment.otherDocuments?.map(item => item.id) }),
        ...((sportEquipment?.mainPhoto?.id) ? { mainPhotoId: sportEquipment.mainPhoto.id } : sportEquipment?.photos?.length > 0 && { mainPhotoId: sportEquipment?.photos?.[0]?.id }),
        ...(sportEquipment?.photos && { photosIds: sportEquipment.photos?.map(item => item.id) }),
        ...(sportEquipment?.barcodePhoto?.id && { barcodePhotoId: sportEquipment.barcodePhoto.id })
      } as CreateSportEquipmentInput));


      if (sportEquipment?.photos?.length > 0) {
        const mainPhoto = sportEquipment?.photos?.find(item => item.id === sportEquipment?.mainPhoto?.id);
        if (!mainPhoto)
          handleSetSportEquipment(prevState => ({ ...prevState, mainPhoto: sportEquipment?.photos?.[0] }));
      }
    }
  }, [sportEquipment, handleSetSportEquipment]);

  return (
    <>
      <PageDialog
        title={sportEquipment?.name === "" ? "Добавление спортивного оборудования" : sportEquipment?.name}
        onClose={onClose}
        maxWidth={"lg"}
        ActionsComponent={Actions}
        ActionsProps={{
          isSubmitting: isSubmitting,
          handleSubmit: () => handleSubmit(),
          errors: errors,
          isValid: isValid,
          sportEquipment: sportEquipment
        }}
      >
        <FormikProvider value={formik}>

          <Grid
            container
            spacing={4}
            px={3}
            pb={3}
          >
            <Grid
              item
              lg={2.5}
              md={3.5}
            >
              <StickyBox
                offsetTop={11}
                offsetBottom={11}
              >
                <Stack
                  spacing={2}
                  mt={2}
                >

                  <Card
                    sx={{ p: 2 }}
                    elevation={2}
                  >
                    <CommissioningDate
                      touched={touched}
                      errors={errors}

                      handleBlur={handleBlur}
                      isEditable

                      date={dayjs(sportEquipment?.commissioningDate)}
                      setDate={(date: Dayjs | null) => {
                        handleSetSportEquipment(prevState => ({
                            ...prevState,
                            commissioningDate: dayjs(date).format("YYYY-MM-DD")
                          })
                        );
                      }}
                    />
                  </Card>

                  <Card
                    sx={{ p: 2 }}
                    elevation={2}
                  >

                    <SportEquipmentLocation
                      vertical
                      touched={touched}

                      handleBlur={handleBlur}
                      errors={errors}
                      isEditable={sportEquipment?.isEditable}
                      location={{
                        sportObject: sportEquipment?.sportObject,
                        sportZone: sportEquipment?.sportZone,
                        sportSubZone: sportEquipment?.sportSubZone
                      }}
                      onChangeLocation={(sportObject, sportZone, sportSubZone) => {
                        handleSetSportEquipment(prevState => ({
                            ...prevState,
                            sportObject,
                            sportZone,
                            sportSubZone
                          })
                        );
                        // Синхронизируем значения локации с formik
                        setFieldValue('sportObjectId', sportObject?.id || null);
                        setFieldValue('sportZoneId', sportZone?.id || null);
                        setFieldValue('sportSubZoneId', sportSubZone?.id || null);
                      }}
                    />
                    {/* Предупреждение: используемое оборудование нельзя разместить на складе */}
                    {sportEquipment?.sportObject && sportEquipment?.inUse && (!sportEquipment?.sportZone || !sportEquipment?.sportSubZone) && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Используемое оборудование нельзя разместить на складе. Укажите спортзону или снимите оборудование с использования.
                      </Alert>
                    )}
                  </Card>

                  <Card
                    sx={{ p: 2 }}
                    elevation={2}
                  >
                    <SportEquipmentCategory
                      vertical
                      touched={touched}
                      errors={errors}
                      isEditable={sportEquipment?.isEditable}
                      sportEquipment={sportEquipment}
                      setSportEquipment={handleSetSportEquipment}
                      handleBlur={handleBlur}
                    />
                  </Card>

                </Stack>
              </StickyBox>
            </Grid>
            <Grid
              item
              lg={9.5}
              md={8.5}
            >
              <Stack
                spacing={4}
                mt={2}
              >
                <SportEquipmentAboutInfo
                  errors={errors}
                  sportEquipment={sportEquipment}
                  setSportEquipment={handleSetSportEquipment}
                  isEditable
                  handleBlur={handleBlur}
                  touched={touched}
                />

                <Card
                  sx={{ p: 3 }}
                  elevation={2}
                >
                  <SportEquipmentPhotos
                    isEditable
                    sportEquipmentPhotos={sportEquipment?.photos || []}
                    setSportEquipmentPhotos={(photos: ImageStoreEntity[]) => handleSetSportEquipment(prevState => {
                      return {
                        ...prevState,
                        photos: photos as ImageStoreEntity[]
                      };
                    })}
                    mainPhoto={sportEquipment?.mainPhoto}
                    setMainPhoto={(mainPhoto) => handleSetSportEquipment(prevState => ({
                      ...prevState,
                      mainPhoto: mainPhoto as ImageStoreEntity
                    }))}
                  />
                </Card>

                <Card
                  sx={{ p: 3 }}
                  elevation={2}
                >
                  <BarcodePhotoUpload
                    isEditable
                    barcodePhoto={sportEquipment?.barcodePhoto}
                    setBarcodePhoto={(photo) => {
                      handleSetSportEquipment(prevState => ({
                        ...prevState,
                        barcodePhoto: photo as ImageStoreEntity
                      }));
                      // Обновляем barcodePhotoId в formik при изменении barcodePhoto
                      setFieldValue('barcodePhotoId', photo?.id || undefined);
                    }}
                  />
                </Card>

                <SportEquipmentDocuments
                  isEditable
                  documents={{
                    ...(sportEquipment?.certificate?.length > 0 && { certificate: sportEquipment?.certificate }),
                    ...(sportEquipment?.commissioningCertificates?.length > 0 && { commissioningCertificate: sportEquipment?.commissioningCertificates }),
                    ...(sportEquipment?.guarantee?.length > 0 && { guarantee: sportEquipment?.guarantee }),
                    ...(sportEquipment?.manual?.length > 0 && { manual: sportEquipment?.manual }),
                    ...(sportEquipment?.passport?.length > 0 && { passport: sportEquipment?.passport }),
                    ...(sportEquipment?.otherDocuments?.length > 0 && { otherDocuments: sportEquipment?.otherDocuments })
                  } as ISportEquipmentDocuments}
                  setDocuments={(documents: ISportEquipmentDocuments) => handleSetSportEquipment(prevState => ({
                    ...prevState,
                    certificate: documents?.certificate,
                    commissioningCertificates: documents?.commissioningCertificate,
                    guarantee: documents?.guarantee,
                    passport: documents?.passport,
                    manual: documents?.manual,
                    otherDocuments: documents?.otherDocuments
                  }))}
                />
              </Stack>
            </Grid>
          </Grid>


        </FormikProvider>

      </PageDialog>
    </>
  );
};

export default AddSportEquipment;